/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useRegistrationDetailsModal } from '../../context'
import { Spin, Table, App, Dropdown } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import {
  CloseCircleOutlined,
  WalletOutlined,
  DollarOutlined,
  FilePdfOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import IndicatorCard from 'src/components/ts-components/IndicatorCard'
import Button from 'src/components/ts-components/Button'
import { formatLongDate } from '@sportninja/common/ts-utils/date-utils'
import { ROUTES } from '@sportninja/common/constants/app'
import LoadingSpinner from 'src/components/LoadingSpinner'
import req from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'
import RefundModal from '../../components/RefundModal'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import NewPaymentModal from '../../components/NewPaymentModal'

async function fetchBillingInfo(billingId) {
  const response = await req(`/billing/schedule/${billingId}/receipt`)
  return response.data
}

const tableStyles = css`
  .ant-table {
    background: transparent;
    border-radius: 8px;
    overflow: hidden;
  }
  .ant-table-thead {
    height: 32px !important;
  }
  .ant-table-thead > tr > th {
    background-color: ${colors.SECONDARY_200} !important;
    color: ${colors.NEUTRAL_70};
    text-transform: uppercase;
    white-space: nowrap;
    /* padding: 16px 8px !important; */
  }
  .ant-table-thead > tr > th:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  .ant-table-thead > tr > th:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  .ant-table-tbody {
    background-color: transparent;
  }
  .ant-table-tbody > tr {
    border-radius: 8px;
  }
  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: ${colors.SECONDARY_400};
  }
  .ant-table-tbody > tr:nth-of-type(odd) {
    background-color: ${colors.SECONDARY_300};
  }
  .ant-table-tbody > tr > td:first-of-type {
    /* border-top-left-radius: 8px;
    border-bottom-left-radius: 8px; */
  }
  .ant-table-tbody > tr > td:last-child {
    /* border-top-right-radius: 8px;
    border-bottom-right-radius: 8px; */
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: none !important;
  }
  .ant-table-tbody > tr > td {
    /* border-bottom: 1px solid ${colors.SECONDARY_500} !important; */
  }
  .ant-table-cell {
    vertical-align: middle !important;
  }
  th.ant-table-cell::before {
    display: none !important;
  }
  .expanded-row {
    background-color: ${colors.SECONDARY_400} !important;
  }
  .ant-table-row-expand-icon-cell {
    width: 20px !important;
    padding: 4px 4px !important;
  }
  .ant-table-expanded-row > td {
    padding: 0 !important;
  }
  .ant-table-row-expand-icon {
    background-color: transparent;
    border-color: ${colors.PRIMARY};
    &::before,
    &::after {
      background-color: ${colors.PRIMARY};
    }
  }
  .ant-table-filter-trigger {
    color: ${colors.NEUTRAL_70};
    &:hover {
      color: ${colors.PRIMARY};
    }
  }
  .ant-dropdown {
    z-index: 12500 !important;
  }
  .ant-table-filter-dropdown {
    z-index: 11500 !important;
    background: ${colors.SECONDARY_300};
    border-color: ${colors.SECONDARY_100};
    .ant-dropdown-menu {
      background: ${colors.SECONDARY_300};
      .ant-dropdown-menu-item {
        color: ${colors.WHITE};
        &:hover {
          background: ${colors.SECONDARY_200};
        }
      }
    }
    .ant-checkbox-wrapper {
      color: ${colors.WHITE};
    }
    .ant-btn-primary {
      background: ${colors.PRIMARY};
      &:hover {
      }
    }
    .ant-btn-link {
      color: ${colors.PRIMARY};
      &:hover {
      }
    }
  }
`

const RegistrationBillingForm = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const { message } = App.useApp()
  const downloadRef = useRef(null)
  const [loadingPdfIds, setLoadingPdfIds] = useState<string[]>([])
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const [refundBilling, setRefundBilling] =
    useState<BillingScheduleItem | null>(null)
  const [filteredData, setFilteredData] = useState<BillingScheduleItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isNewPaymentModalOpen, setIsNewPaymentModalOpen] = useState(false)

  const { registrationDetails, loading, error, types, refetch } =
    useRegistrationDetailsModal()

  const isPlayerRegistration = useMemo(
    () => registrationDetails?.registration_type === 'Player',
    [registrationDetails?.registration_type]
  )

  const currency = useMemo(() => {
    if (!registrationDetails?.billing?.[0]?.currency) {
      return null
    }
    return types?.currency?.find(
      (c) => Number(c.id) === registrationDetails.billing[0].currency
    )?.name
  }, [registrationDetails, types])

  const totalOutstanding = useMemo(() => {
    if (!registrationDetails?.billing) {
      return 0
    }

    let total = 0
    registrationDetails?.billing
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .filter((el) => !el.paid)
      .forEach((el) => {
        total += el.subtotal
      })

    return total
  }, [isPlayerRegistration, registrationDetails?.billing])

  const totalPaid = useMemo(() => {
    if (!registrationDetails?.billing) {
      return 0
    }

    let total = 0
    registrationDetails?.billing
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .filter((el) => el.paid)
      .forEach((el) => {
        total += el.subtotal
      })

    return total
  }, [isPlayerRegistration, registrationDetails?.billing])

  const calculatePastDues = (billingDetails) => {
    let total = 0

    billingDetails
      ?.filter((el) =>
        isPlayerRegistration
          ? el.user_type === 'player'
          : el.user_type === 'teamRep'
      )
      .forEach((el) => {
        if (el.paid) {
          return
        }

        const day = dayjs(el?.due_date, 'YYYY-MM-DD')
        const isAfter = dayjs().isAfter(day)

        if (isAfter) {
          total += el.subtotal
        }
      })

    return total
  }

  const onView = useCallback(
    async (billingId: string) => {
      setLoadingPdfIds((prev) => [...prev, billingId])
      try {
        const response = await fetchBillingInfo(billingId)
        const pdf = await req(`/billing/schedule/${billingId}/receipt/pdf`, {
          parseJSON: false,
        })
        const normalizePdf = await pdf.blob()
        const link = document.createElement('a')
        link.href = URL.createObjectURL(normalizePdf)
        link.download = `${response?.charge?.stripe_id}.pdf`
        link.click()
      } catch (err) {
        console.error(err)
        message.open({
          type: 'error',
          content: getErrorMessage(err),
        })
      } finally {
        setLoadingPdfIds((prev) => prev.filter((id) => id !== billingId))
      }
    },
    [setLoadingPdfIds, message]
  )

  const columns: ColumnsType<BillingScheduleItem> = useMemo(
    () =>
      [
        {
          title: 'Type',
          dataIndex: 'payment_type',
          key: 'payment_type',
          // filters: [
          //   { text: 'One Time Payment', value: 'one_time' },
          //   { text: 'Deposit', value: 'deposit' },
          //   { text: 'Installment Payment', value: 'installment' },
          // ],
          // onFilter: (value: string, record: BillingScheduleItem) => {
          //   if (record.single_payment) {
          //     return value === 'one_time'
          //   }
          //   return record.payment_type === value
          // },
          render: (_text: string, record: BillingScheduleItem) => (
            <span>
              {record.single_payment
                ? 'One Time Payment'
                : record.payment_type === 'deposit'
                ? 'Deposit'
                : 'Installment Payment'}
            </span>
          ),
        },
        {
          title: 'Due Date',
          dataIndex: 'due_date',
          key: 'due_date',
          sorter: (a, b) => dayjs(a.due_date).unix() - dayjs(b.due_date).unix(),
          // filters: [
          //   { text: 'Past Due', value: 'past_due' },
          //   { text: 'Due Today', value: 'today' },
          //   { text: 'Future', value: 'future' },
          // ],
          // onFilter: (value: string, record: BillingScheduleItem) => {
          //   const dueDate = dayjs(record.due_date)
          //   const today = dayjs()

          //   switch (value) {
          //     case 'past_due':
          //       return dueDate.isBefore(today, 'day') && !record.paid
          //     case 'today':
          //       return dueDate.isSame(today, 'day')
          //     case 'future':
          //       return dueDate.isAfter(today, 'day')
          //     default:
          //       return true
          //   }
          // },
          render: (date: string, record: BillingScheduleItem) => (
            <span
              css={css`
                color: ${dayjs(date).isBefore(dayjs()) && !record.paid
                  ? colors.ERROR_200
                  : colors.WHITE};
              `}
            >
              {formatLongDate(date)}
            </span>
          ),
        },
        {
          title: 'Subtotal',
          dataIndex: 'subtotal',
          key: 'subtotal',
          sorter: (a, b) => a.subtotal - b.subtotal,
          render: (amount: number) => (
            <span>{currency && formatCurrency(amount, currency)}</span>
          ),
        },
        {
          title: 'Tax',
          dataIndex: 'taxtotal',
          key: 'taxtotal',
          sorter: (a, b) => a.taxtotal - b.taxtotal,
          render: (amount: number) => (
            <span>{currency && formatCurrency(amount, currency)}</span>
          ),
        },
        {
          title: 'Total',
          dataIndex: 'total',
          key: 'total',
          sorter: (a, b) => a.total - b.total,
          render: (amount: number) => (
            <span>{currency && formatCurrency(amount, currency)}</span>
          ),
        },
        // {
        //   title: 'Currency',
        //   dataIndex: 'currency',
        //   key: 'currency',
        //   render: (_currencyId: number) => <span>{currency}</span>,
        // },
        !isMobile && {
          title: 'Inst.',
          key: 'installment',
          render: (_: any, _record: BillingScheduleItem, index: number) => (
            <span>
              {index > 0
                ? `${index}/${
                    registrationDetails?.billing?.length
                      ? registrationDetails?.billing?.length - 1
                      : 0
                  }`
                : '--'}
            </span>
          ),
        },
        // {
        //   title: 'Coupon',
        //   key: 'coupon',
        //   align: 'center',
        //   width: isMobile ? 80 : 100,
        //   render: (_: any, _record: BillingScheduleItem) => {
        //     if (
        //       !registrationDetails?.is_coupon ||
        //       !registrationDetails?.coupon
        //     ) {
        //       return <Tag color='red'>No</Tag>
        //     }

        //     return (
        //       <Tooltip
        //         title={
        //           registrationDetails.coupon
        //             ? `Code: ${registrationDetails.coupon.code} - ${
        //                 registrationDetails.coupon.discount_type ===
        //                 'percentage'
        //                   ? `${registrationDetails.coupon.discount}%`
        //                   : currency &&
        //                     formatCurrency(
        //                       registrationDetails.coupon.discount_amount,
        //                       currency
        //                     )
        //               } ${
        //                 registrationDetails.coupon.discount_type ===
        //                 'percentage'
        //                   ? 'off'
        //                   : 'discount'
        //               }`
        //             : ''
        //         }
        //       >
        //         <Tag color='green'>Yes</Tag>
        //       </Tooltip>
        //     )
        //   },
        // },
        {
          title: 'Status',
          key: 'status',
          align: 'center',
          width: isMobile ? 80 : 100,
          // filters: [
          //   { text: 'Paid', value: 'paid' },
          //   { text: 'Pending', value: 'pending' },
          //   { text: 'Past Due', value: 'past_due' },
          //   { text: 'Refunded', value: 'refunded' },
          // ],
          // onFilter: (value: string, record: BillingScheduleItem) => {
          //   const isPaid = record.paid
          //   const isPastDue =
          //     dayjs(record.due_date).isBefore(dayjs()) && !record.paid
          //   const isRefunded = record?.charge?.refunded

          //   switch (value) {
          //     case 'paid':
          //       return isPaid && !isRefunded
          //     case 'pending':
          //       return !isPaid && !isPastDue
          //     case 'past_due':
          //       return isPastDue
          //     case 'refunded':
          //       return isRefunded
          //     default:
          //       return true
          //   }
          // },
          render: (record: BillingScheduleItem) => {
            const isPaid = record.paid
            const isPastDue =
              dayjs(record.due_date).isBefore(dayjs()) && !record.paid
            const isRefunded = record?.charge?.refunded

            if (isRefunded) {
              return (
                <span
                  css={css`
                    padding: 2px 4px;
                    border-radius: 4px;
                    border: 1px solid ${colors.INFO_500};
                    background-color: ${'rgba(6, 22, 51, 0.50)'};
                    color: ${colors.INFO_200};
                    font-size: ${isMobile ? '12px' : '14px'};
                    white-space: nowrap;
                    display: inline-block;
                    text-align: center;
                    min-width: ${isMobile ? '70px' : '90px'};
                  `}
                >
                  Refunded
                </span>
              )
            }

            return (
              <span
                css={css`
                  padding: 2px 4px;
                  border-radius: 4px;
                  border: 1px solid
                    ${isPaid
                      ? colors.SUCCESS_500
                      : isPastDue
                      ? colors.ERROR_500
                      : colors.WARNING_500};
                  color: ${isPaid
                    ? colors.SUCCESS_200
                    : isPastDue
                    ? colors.ERROR_200
                    : colors.WARNING_200};
                  background-color: ${isPaid
                    ? 'rgba(5, 58, 17, 0.50)'
                    : isPastDue
                    ? 'rgba(51, 6, 6, 0.50)'
                    : 'rgba(51, 32, 6, 0.50)'};
                  font-size: ${isMobile ? '12px' : '14px'};
                  white-space: nowrap;
                  display: inline-block;
                  text-align: center;
                  min-width: ${isMobile ? '70px' : '90px'};
                `}
              >
                {isPaid ? 'Paid' : isPastDue ? 'Past Due' : 'Pending'}
              </span>
            )
          },
        },
        {
          title: 'Action',
          key: 'action',
          align: 'center',
          width: isMobile ? 80 : 120,
          render: (record: BillingScheduleItem) => {
            const dropdownItems = [] as {
              label: string
              key: string
              onClick: () => void
            }[]

            const isRefunded = record?.charge?.refunded

            if (record.paid && !isRefunded) {
              dropdownItems.push({
                label: 'Refund',
                key: 'refund',
                onClick: () => {
                  setRefundBilling(record)
                  setIsRefundModalOpen(true)
                },
              })
            }

            return (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                `}
              >
                {!record.paid && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                      cursor: pointer;
                      border: 1px solid ${colors.PRIMARY};
                      padding: 4px 8px;
                      border-radius: 8px;
                      background-color: ${colors.PRIMARY};
                    `}
                    onClick={() => {
                      window.open(
                        `${ROUTES.REGISTRATION_PAYMENT}?teamId=${registrationDetails?.team?.uid}&rosterId=${registrationDetails?.team?.team_player_roster_id}&billingId=${record.uid}`,
                        '_blank'
                      )
                    }}
                  >
                    <DollarOutlined
                      css={css`
                        font-size: ${isMobile ? '16px' : '20px'};
                        color: ${colors.SECONDARY_300};
                      `}
                    />
                    {!isMobile && (
                      <span
                        css={css`
                          color: ${colors.SECONDARY_300};
                          font-size: 14px;
                        `}
                      >
                        Pay
                      </span>
                    )}
                  </div>
                )}
                {record.paid && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      cursor: pointer;
                      border: 1px solid ${colors.PRIMARY};
                      padding: 8px;
                      border-radius: 8px;
                      background-color: transparent;
                      min-width: ${isMobile ? '40px' : '100px'};
                    `}
                    ref={downloadRef}
                    onClick={() => onView(record?.uid)}
                  >
                    {loadingPdfIds.includes(record.uid) ? (
                      <LoadingSpinner size={1.5} />
                    ) : (
                      <>
                        <FilePdfOutlined
                          css={css`
                            font-size: ${isMobile ? '16px' : '20px'};
                            color: ${colors.PRIMARY};
                          `}
                        />
                        {!isMobile && (
                          <span
                            css={css`
                              color: ${colors.PRIMARY};
                              font-size: 14px;
                            `}
                          >
                            Receipt
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
                {dropdownItems.length > 0 && (
                  <Dropdown
                    menu={{ items: dropdownItems }}
                    trigger={['click']}
                    placement='bottomRight'
                    overlayStyle={{ zIndex: 99999 }}
                  >
                    <MoreOutlined
                      css={css`
                        color: ${colors.PRIMARY};
                        font-size: 20px;
                        cursor: pointer;
                      `}
                    />
                  </Dropdown>
                )}
              </div>
            )
          },
        },
      ].filter(Boolean) as ColumnsType<BillingScheduleItem>,
    [
      isMobile,
      currency,
      registrationDetails?.billing?.length,
      registrationDetails?.team?.uid,
      registrationDetails?.team?.team_player_roster_id,
      loadingPdfIds,
      onView,
    ]
  )

  const expandedRowRender = (record: BillingScheduleItem) => {
    if (!record.charge) {
      return null
    }
    return (
      <div
        css={css`
          padding: 16px;
          background-color: ${colors.SECONDARY_300};
          border-radius: 8px;
          margin: 8px;
          max-height: 300px;
          overflow-y: auto;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <div>
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_70}
              style='text-transform: uppercase;'
            >
              Stripe ID
            </Text>
            <Text variant='paragraphMedium' color={colors.WHITE}>
              {record.charge.stripe_id}
            </Text>
          </div>
          <div>
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_70}
              style='text-transform: uppercase;'
            >
              Description
            </Text>
            <Text variant='paragraphMedium' color={colors.WHITE}>
              {record.charge.description}
            </Text>
          </div>
          <div>
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_70}
              style='text-transform: uppercase;'
            >
              Paid At
            </Text>
            <Text variant='paragraphMedium' color={colors.WHITE}>
              {formatLongDate(record.charge.paid_at)}
            </Text>
          </div>
          <div>
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_70}
              style='text-transform: uppercase;'
            >
              Amount
            </Text>
            <Text variant='paragraphMedium' color={colors.WHITE}>
              {currency &&
                formatCurrency(Number(record.charge.amount), currency)}
            </Text>
          </div>
          <div>
            <Text
              variant='paragraphXSmall'
              color={colors.NEUTRAL_70}
              style='text-transform: uppercase;'
            >
              Status
            </Text>
            <Text variant='paragraphMedium' color={colors.WHITE}>
              {record.charge.refunded
                ? 'Refunded'
                : record.charge.paid
                ? 'Paid'
                : 'Pending'}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  const handleTableChange: TableProps<BillingScheduleItem>['onChange'] = (
    _pagination,
    filters,
    sorter,
    _extra
  ) => {
    let data = [...(registrationDetails?.billing || [])]

    // Apply filters
    Object.keys(filters).forEach((key) => {
      const selectedFilters = filters[key]
      if (selectedFilters && selectedFilters.length > 0) {
        const column = columns.find((col) => col.key === key)
        if (column?.onFilter) {
          data = data.filter((record) =>
            selectedFilters.some((value) => column.onFilter?.(value, record))
          )
        }
      }
    })

    // Apply sorting
    if (sorter && !Array.isArray(sorter)) {
      const { field, order } = sorter
      if (order) {
        const column = columns.find((col) => col.key === field)
        if (column?.sorter) {
          data.sort((a, b) => {
            const result =
              typeof column.sorter === 'function' ? column.sorter(a, b) : 0
            return order === 'descend' ? -result : result
          })
        }
      }
    }

    setFilteredData(data)
  }

  // Handle row selection changes
  const handleSelectionChange = useCallback(
    (selectedKeys: React.Key[], selectedRows: BillingScheduleItem[]) => {
      setSelectedRowKeys(selectedKeys)
      // You can do something with the selected rows here if needed
      console.log('Selected rows:', selectedRows)
    },
    []
  )

  // Row selection configuration
  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleSelectionChange,
      getCheckboxProps: (record: BillingScheduleItem) => ({
        // Optionally disable checkbox for certain rows based on conditions
        disabled: Boolean(record?.charge?.refunded), // Convert to boolean using Boolean()
      }),
    }),
    [selectedRowKeys, handleSelectionChange]
  )

  useEffect(() => {
    setFilteredData(registrationDetails?.billing || [])
  }, [registrationDetails?.billing])

  if (loading && !error) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 50vh;
        `}
      >
        <Spin size='large' style={{ color: colors.PRIMARY }} />
      </div>
    )
  }

  if (error && !loading) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 50vh;
          gap: 24px;
        `}
      >
        <CloseCircleOutlined
          style={{ fontSize: 48, color: colors.FIERY_RED }}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          `}
        >
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            An error occurred while loading registration details
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {error?.message ||
              'Unable to load registration details. Please try again later.'}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <>
      <RefundModal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        billing={refundBilling}
        refetch={refetch}
        currency={currency}
      />
      <NewPaymentModal
        isOpen={isNewPaymentModalOpen}
        onClose={() => setIsNewPaymentModalOpen(false)}
        refetch={refetch}
        currency={currency}
      />
      <div
        css={css`
          padding: ${isMobile ? '8px' : '0'};
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow-y: auto;
          padding: 0 16px;
          margin-top: 20px;
          .ant-dropdown {
            z-index: 20000 !important;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: ${isMobile ? 'column' : 'row'};
            gap: 16px;
            margin-bottom: 16px;
            width: 100%;
            overflow: visible;
          `}
        >
          <IndicatorCard
            icon={<WalletOutlined />}
            title='Total Paid'
            value={
              currency
                ? `${formatCurrency(totalPaid, currency)} / ${formatCurrency(
                    totalOutstanding + totalPaid,
                    currency
                  )}`
                : ''
            }
            subtitle={`Total Outstanding: ${
              currency ? formatCurrency(totalOutstanding, currency) : ''
            }`}
            hideSmallIndicator={true}
            dynamicHeight={true}
            alternateBackgroundColor={colors.SECONDARY_200}
          />

          {!!registrationDetails?.billing?.find((el) => !el.paid)?.subtotal && (
            <IndicatorCard
              icon={<WalletOutlined />}
              title='Next Payment'
              value={
                currency
                  ? formatCurrency(
                      registrationDetails?.billing?.find((el) => !el.paid)
                        ?.subtotal || 0,
                      currency
                    )
                  : ''
              }
              subtitle={formatLongDate(
                registrationDetails?.billing?.find((el) => !el.paid)
                  ?.due_date || ''
              )}
              hideSmallIndicator={true}
              dynamicHeight={true}
              alternateBackgroundColor={colors.SECONDARY_200}
            />
          )}

          <IndicatorCard
            icon={<WalletOutlined />}
            title='Total Past Due'
            value={
              currency
                ? formatCurrency(
                    calculatePastDues(registrationDetails?.billing || []),
                    currency
                  )
                : ''
            }
            subtitle=''
            hideSmallIndicator={true}
            dynamicHeight={true}
            alternateBackgroundColor={colors.SECONDARY_200}
          />
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;
            display: none;
          `}
        >
          <Button
            text='New +'
            onClick={() => setIsNewPaymentModalOpen(true)}
            variant='quaternary'
            style={`
              max-width: 100px !important;
            `}
          />
          <Button
            text='Refund'
            onClick={() => {}}
            variant='quaternary'
            style={`
              max-width: 100px !important;
            `}
          />
        </div>

        <div
          css={css`
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          `}
        >
          <Table
            rowSelection={rowSelection}
            rowKey={(record) => record.uid}
            // @ts-ignore
            columns={columns}
            dataSource={filteredData}
            pagination={false}
            scroll={{ x: 'max-content' }}
            size='small'
            bordered={false}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => !!record.charge,
              expandedRowClassName: () => 'expanded-row',
            }}
            onChange={handleTableChange}
            css={tableStyles}
          />
        </div>
      </div>
    </>
  )
}

export default RegistrationBillingForm
